<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui'
import type { ContactFormSchemaType } from '~~/shared/validation'

import { ContactFormSchema } from '~~/shared/validation'

definePageMeta({ prerender: true })

const { data: contact } = await useAsyncData('contact', () => queryCollection('contact').first())

useSeoMeta({
  description: contact.value?.hero.description,
  title: contact.value?.hero.title,
})

const state = reactive<Partial<ContactFormSchemaType>>({
  email: '',
  message: '',
  name: '',
})

const pending = ref(false)
const success = ref(false)
const formError = ref<null | string>(null)
const { trackEvent } = useAnalytics()

const isFormValid = computed(() => ContactFormSchema.safeParse(state).success)

async function onSubmit(event: FormSubmitEvent<ContactFormSchemaType>) {
  pending.value = true
  formError.value = null
  trackEvent('contact_form_submitted')

  try {
    await $fetch('/api/contact', {
      body: event.data,
      method: 'POST',
    })
    success.value = true
    trackEvent('contact_form_success')
  } catch {
    formError.value = contact.value?.form.errorMessage ?? 'Something went wrong. Please try again.'
    trackEvent('contact_form_error')
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <div v-if="contact">
    <!-- ── Kaweah Tech Brand Hero ─────────────────────────────────── -->
    <UPageSection>
      <div class="grid items-center gap-12 lg:grid-cols-2">
        <!-- Left: branding, description, features, CTA -->
        <div class="flex flex-col gap-8">
          <div class="space-y-4">
            <h1 class="text-4xl font-bold tracking-tight">
              {{ contact.hero.title }}
            </h1>
            <p class="text-lg leading-relaxed text-muted">
              {{ contact.hero.description }}
            </p>
          </div>

          <ul class="space-y-5">
            <li
              v-for="feature in contact.hero.features"
              :key="feature.title"
              class="flex items-start gap-3"
            >
              <UIcon
                :name="feature.icon"
                class="text-primary mt-0.5 size-5 shrink-0"
              />
              <div>
                <p class="font-semibold">
                  {{ feature.title }}
                </p>
                <p class="text-sm text-muted">
                  {{ feature.description }}
                </p>
              </div>
            </li>
          </ul>

          <div class="flex flex-wrap gap-3">
            <UButton
              v-for="link in contact.hero.links"
              :key="link.label"
              :color="link.color"
              :icon="link.icon"
              :label="link.label"
              :rel="link.target === '_blank' ? 'noopener noreferrer' : undefined"
              :size="link.size ?? 'lg'"
              :target="link.target"
              :to="link.to"
              :variant="link.variant"
            />
          </div>
        </div>

        <!-- Right: brand image -->
        <div class="overflow-hidden rounded-2xl shadow-2xl ring-1 ring-white/10">
          <img
            :src="contact.hero.imageUrl"
            alt="Kaweah Tech"
            class="w-full object-cover"
          >
        </div>
      </div>
    </UPageSection>

    <!-- ── Contact Form ───────────────────────────────────────────── -->
    <UPageSection>
      <!-- Two-column form layout -->
      <div class="grid items-start gap-12 lg:grid-cols-5">
        <!-- Left: context panel (2 cols) -->
        <div class="flex flex-col gap-8 lg:col-span-2">
          <div class="space-y-3">
            <h2 class="text-3xl font-bold">
              {{ contact.form.title }}
            </h2>
            <p class="text-muted">
              {{ contact.form.description }}
            </p>
          </div>

          <USeparator/>

          <!-- Icon: lite for light mode, dark for dark mode -->
          <UColorModeImage
            light="https://assets.kaweah.tech/kaweah-tech-logo-dark.svg"
            dark="https://assets.kaweah.tech/kaweah-tech-logo-lite.svg"
            class="max-h-10 self-start object-contain md:max-h-12"
          />

          <p class="text-sm text-muted">
            {{ contact.form.note }}
          </p>
        </div>

        <!-- Right: success state or form card (3 cols) -->
        <div class="lg:col-span-3">
          <!-- Success state -->
          <div
            v-if="success"
            class="flex flex-col items-center gap-6 py-16 text-center"
          >
            <UIcon
              class="text-primary size-16"
              name="mdi:check-circle-outline"
            />
            <div class="space-y-2">
              <h2 class="text-2xl font-bold">
                {{ contact.form.success.title }}
              </h2>
              <p class="text-muted">
                {{ contact.form.success.description }}
              </p>
            </div>
            <UButton
              :label="contact.form.success.backLabel"
              icon="mdi:arrow-left"
              to="/"
              variant="outline"
            />
          </div>

          <!-- Form card -->
          <UCard
            v-else
            :ui="{ body: 'p-8 sm:p-10 bg-muted/30' }"
          >
            <UForm
              :schema="ContactFormSchema"
              :state="state"
              class="flex flex-col gap-6"
              @submit="onSubmit"
            >
              <UFormField
                :label="contact.form.fields.name.label"
                name="name"
                required
              >
                <UInput
                  v-model="state.name"
                  :placeholder="contact.form.fields.name.placeholder"
                  class="w-full"
                  size="lg"
                />
              </UFormField>

              <UFormField
                :label="contact.form.fields.email.label"
                name="email"
                required
              >
                <UInput
                  v-model="state.email"
                  :placeholder="contact.form.fields.email.placeholder"
                  class="w-full"
                  size="lg"
                  type="email"
                />
              </UFormField>

              <UFormField
                :label="contact.form.fields.message.label"
                name="message"
                required
              >
                <UTextarea
                  v-model="state.message"
                  :placeholder="contact.form.fields.message.placeholder"
                  :rows="7"
                  class="w-full"
                  size="lg"
                />
              </UFormField>

              <UAlert
                v-if="formError"
                :description="formError"
                color="error"
                icon="mdi:alert-circle-outline"
                variant="soft"
              />

              <UButton
                :disabled="!isFormValid || pending"
                :label="contact.form.submit"
                :loading="pending"
                class="self-start"
                size="lg"
                trailing-icon="mdi:send"
                type="submit"
              />
            </UForm>
          </UCard>
        </div>
      </div>
    </UPageSection>
  </div>
</template>
